import { useState } from 'react';
import { X } from 'lucide-react';

interface CreateStoreModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (storeData: any) => void;
    regionalManagers: Array<{ id: number; name: string }>;
    areaManagers: Array<{ id: number; name: string }>;
}

export default function CreateStoreModal({
    isOpen,
    onClose,
    onSubmit,
    regionalManagers,
    areaManagers
}: CreateStoreModalProps) {
    const [formData, setFormData] = useState({
        name: '',
        storenumber: '',
        store_email: '',
        store_type: '',
        classification_type: '',
        country: '',
        province: '',
        address: '',
        regional_id: '',
        area_id: '',
        area: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Create New Store</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Store Name</label>
                        <input
                            type="text"
                            required
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Store Number</label>
                        <input
                            type="text"
                            required
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.storenumber}
                            onChange={(e) => setFormData({ ...formData, storenumber: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.store_email}
                            onChange={(e) => setFormData({ ...formData, store_email: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Store Type</label>
                        <select
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.store_type}
                            onChange={(e) => setFormData({ ...formData, store_type: e.target.value })}
                        >
                            <option value="">Select Type</option>
                            <option value="retail">Retail</option>
                            <option value="wholesale">Wholesale</option>
                            <option value="franchise">Franchise</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Classification</label>
                        <select
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.classification_type}
                            onChange={(e) => setFormData({ ...formData, classification_type: e.target.value })}
                        >
                            <option value="">Select Classification</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="C">C</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Country</label>
                        <input
                            type="text"
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.country}
                            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Province</label>
                        <input
                            type="text"
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.province}
                            onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                        />
                    </div>

                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Address</label>
                        <textarea
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            rows={3}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Regional Manager</label>
                        <select
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.regional_id}
                            onChange={(e) => setFormData({ ...formData, regional_id: e.target.value })}
                        >
                            <option value="">Select Regional Manager</option>
                            {regionalManagers.map(manager => (
                                <option key={manager.id} value={manager.id}>{manager.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Area Manager</label>
                        <select
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.area_id}
                            onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                        >
                            <option value="">Select Area Manager</option>
                            {areaManagers.map(manager => (
                                <option key={manager.id} value={manager.id}>{manager.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Area</label>
                        <input
                            type="text"
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            value={formData.area}
                            onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                        />
                    </div>

                    <div className="col-span-2 flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Create Store
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 